import { Injectable, signal } from '@angular/core';
import { environment } from '../../../environments/environment';

export interface Certificado {
  name: string;
  description: string;
  category: string;
  index: number;
}

@Injectable({
  providedIn: 'root',
})
export class CertificadosService {
  private readonly certificados = signal<Certificado[]>([]);
  private readonly isLoading = signal<boolean>(false);
  private readonly error = signal<string | null>(null);

  // Señales públicas de solo lectura
  readonly certificadosSignal = this.certificados.asReadonly();
  readonly isLoadingSignal = this.isLoading.asReadonly();
  readonly errorSignal = this.error.asReadonly();

  /**
   * Obtiene la lista de certificados desde el backend
   */
  async getCertificados(): Promise<Certificado[]> {
    this.isLoading.set(true);
    this.error.set(null);

    try {
      const response = await fetch(`${environment.apiUrl}/certificates`, {
        method: 'GET',
        credentials: 'include', // REQUIRED for cookies
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({
          message: `Error ${response.status}: ${response.statusText}`,
        }));
        throw new Error(errorData.message || 'Error al obtener certificados');
      }

      const responseData: { success: boolean; data: Certificado[]; message: string } = await response.json();
      
      // Ordenar por índice (crear copia para no mutar el original)
      const sortedData = [...responseData.data].sort((a: Certificado, b: Certificado) => a.index - b.index);
      
      this.certificados.set(sortedData);
      this.isLoading.set(false);
      
      return sortedData;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error desconocido al cargar certificados';
      this.error.set(errorMessage);
      this.isLoading.set(false);
      throw err;
    }
  }

  /**
   * Limpia el estado de certificados
   */
  clearCertificados(): void {
    this.certificados.set([]);
    this.error.set(null);
  }

  /**
   * Descarga un certificado en formato PDF Base64
   * @param index Índice del certificado (para cotizaciones usar 1)
   * @param requestData Datos del formulario con numeroMeses, tipoProducto, tipoConsulta
   * @returns Promise con el PDF en Base64
   */
  async downloadCertificate(index: number, requestData: Record<string, any>): Promise<string> {
    try {
      const response = await fetch(`${environment.apiUrl}/certificates/${index}/download`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({
          message: `Error ${response.status}: ${response.statusText}`,
        }));
        throw new Error(errorData.message || 'Error al descargar certificado');
      }

      const responseData = await response.json();
      return responseData.resultado; // PDF Base64
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error desconocido al descargar certificado';
      this.error.set(errorMessage);
      throw err;
    }
  }

  /**
   * Envía un certificado por email
   * @param index Índice del certificado (para cotizaciones usar 1)
   * @param requestData Datos del formulario con numeroMeses, tipoProducto, tipoConsulta
   * @returns Promise con el mensaje de confirmación
   */
  async sendCertificateByEmail(index: number, requestData: Record<string, any>): Promise<string> {
    try {
      const response = await fetch(`${environment.apiUrl}/certificates/${index}/email`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({
          message: `Error ${response.status}: ${response.statusText}`,
        }));
        throw new Error(errorData.message || 'Error al enviar certificado por email');
      }

      const responseData = await response.json();
      return responseData.mensaje;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error desconocido al enviar certificado';
      this.error.set(errorMessage);
      throw err;
    }
  }
}
